"use client";

import { useState, useEffect, useCallback, useMemo, useRef, Suspense } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Badge } from "@/app/components/ui/badge";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { ArrowRight, RefreshCw, TrendingUp, TrendingDown, DollarSign, Bitcoin, AlertTriangle, Loader2, History, Star, ArrowLeftRight } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useQueryParam } from "@/lib/routing";
import { useAuth } from "@/hooks/useAuth";

const CURRENCY_TABS = ["converter", "favorites", "history"] as const;
type CurrencyTabValue = typeof CURRENCY_TABS[number];

interface Currency {
  code: string;
  name: string;
  symbol: string;
  type: 'fiat' | 'crypto';
  flag?: string;
}

interface ConversionHistory {
  id: string;
  from: string;
  to: string;
  amount: number;
  result: number;
  rate: number;
  timestamp: Date;
}

interface PopularConversion {
  from: string;
  to: string;
  rate: number;
  trend: 'up' | 'down' | 'stable';
}

function CurrencyConverterPageContent() {
  const [amount, setAmount] = useState("1");
  const [fromCurrency, setFromCurrency] = useState("USD");
  const [toCurrency, setToCurrency] = useState("EUR");
  const [result, setResult] = useState<number | null>(null);
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [conversionHistory, setConversionHistory] = useState<ConversionHistory[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  // Single-param state for `tab` with type safety
  const [activeTab, setActiveTab] = useQueryParam<CurrencyTabValue>("tab", {
    parse: (raw) => (raw ?? "converter") as CurrencyTabValue,
    serialize: (v) => v ?? null,
    defaultValue: "converter",
    replace: true, // keep history clean while switching tabs
  });
  const conversionInProgress = useRef(false);
  const { user } = useAuth();
  const utils = trpc.useUtils();

  // tRPC mutations and queries
  const convertCurrencyMutation = trpc.currency.convert.useMutation();
  const supportedCurrenciesQuery = trpc.currency.getSupportedCurrencies.useQuery();
  const popularConversionsQuery = trpc.currency.getPopularConversions.useQuery();
  const serverHistoryQuery = trpc.currency.getConversionHistory.useQuery(undefined, {
    enabled: !!user,
  });
  const clearServerHistoryMutation = trpc.currency.clearConversionHistory.useMutation({
    onSuccess: () => utils.currency.getConversionHistory.invalidate(),
  });
  
  // Get current exchange rate for the selected pair
  const exchangeRateQuery = trpc.currency.getExchangeRate.useQuery(
    { fromCurrency, toCurrency },
    { 
      enabled: !!fromCurrency && !!toCurrency,
      refetchInterval: 60000, // Refetch every minute
      staleTime: 30000, // Consider data stale after 30 seconds
    }
  );

  // Enhanced currency data with flags - now from tRPC
  const currencies: Currency[] = useMemo(() => {
    if (!supportedCurrenciesQuery.data) return [];
    
    const fiatCurrencies = supportedCurrenciesQuery.data.fiat.map(currency => ({
      ...currency,
      type: "fiat" as const,
    }));
    
    const cryptoCurrencies = supportedCurrenciesQuery.data.crypto.map(currency => ({
      ...currency,
      type: "crypto" as const,
    }));
    
    return [...fiatCurrencies, ...cryptoCurrencies];
  }, [supportedCurrenciesQuery.data]);

  // Popular conversions with trends - now from tRPC
  const popularConversions: PopularConversion[] = useMemo(() => {
    if (!popularConversionsQuery.data) return [];
    return popularConversionsQuery.data;
  }, [popularConversionsQuery.data]);

  const displayHistory: ConversionHistory[] = useMemo(() => {
    if (user && serverHistoryQuery.data) {
      return serverHistoryQuery.data.map((row) => ({
        id: row.id,
        from: row.fromCurrency,
        to: row.toCurrency,
        amount: row.amount,
        result: row.convertedAmount,
        rate: row.exchangeRate,
        timestamp:
          row.createdAt instanceof Date ? row.createdAt : new Date(row.createdAt as string),
      }));
    }
    return conversionHistory;
  }, [user, serverHistoryQuery.data, conversionHistory]);

  // Load conversion history from localStorage when signed out; favorites always from localStorage
  useEffect(() => {
    if (user) return;

    const savedHistory = localStorage.getItem("conversionHistory");
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory);
        if (Array.isArray(parsed)) {
          const historyWithDates = parsed.map((item: ConversionHistory) => ({
            ...item,
            timestamp: new Date(item.timestamp),
          }));
          setConversionHistory(historyWithDates);
        }
      } catch (e) {
        console.error("Failed to parse conversion history", e);
      }
    }
  }, [user]);

  useEffect(() => {
    const savedFavorites = localStorage.getItem("favoriteCurrencies");
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (e) {
        console.error("Failed to parse favorite currencies", e);
      }
    }
  }, []);

  // Persist conversion history locally only for signed-out users
  useEffect(() => {
    if (user) return;
    localStorage.setItem("conversionHistory", JSON.stringify(conversionHistory));
  }, [conversionHistory, user]);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("favoriteCurrencies", JSON.stringify(favorites));
  }, [favorites]);

  // Update exchange rate display when query data changes
  useEffect(() => {
    if (exchangeRateQuery.data) {
      setExchangeRate(exchangeRateQuery.data.rate);
      // Ensure lastUpdated is a proper Date object
      const lastUpdatedDate = exchangeRateQuery.data.lastUpdated instanceof Date 
        ? exchangeRateQuery.data.lastUpdated 
        : new Date(exchangeRateQuery.data.lastUpdated);
      setLastUpdated(lastUpdatedDate);
    }
  }, [exchangeRateQuery.data]);

  // Debounced conversion function using tRPC
  const convertCurrency = useCallback(async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    // Prevent multiple simultaneous conversions
    if (conversionInProgress.current) {
      return;
    }

    conversionInProgress.current = true;
    setIsLoading(true);
    setError("");

    try {
      const result = await convertCurrencyMutation.mutateAsync({
        amount: parseFloat(amount),
        fromCurrency,
        toCurrency,
      });

      setResult(result.convertedAmount);
      setExchangeRate(result.exchangeRate);
      
      // Ensure lastUpdated is a proper Date object
      const lastUpdatedDate = result.lastUpdated instanceof Date 
        ? result.lastUpdated 
        : new Date(result.lastUpdated);
      setLastUpdated(lastUpdatedDate);

      if (user) {
        await utils.currency.getConversionHistory.invalidate();
      } else {
        const historyItem: ConversionHistory = {
          id: Date.now().toString(),
          from: fromCurrency,
          to: toCurrency,
          amount: parseFloat(amount),
          result: result.convertedAmount,
          rate: result.exchangeRate,
          timestamp: lastUpdatedDate,
        };
        setConversionHistory((prev) => [historyItem, ...prev.slice(0, 9)]);
      }
    } catch (error: any) {
      setError(error.message || "Failed to convert currency. Please try again.");
    } finally {
      setIsLoading(false);
      conversionInProgress.current = false;
    }
  }, [amount, fromCurrency, toCurrency, convertCurrencyMutation, user, utils.currency.getConversionHistory]);

  // Swap currencies
  const swapCurrencies = useCallback(() => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setResult(null);
    setExchangeRate(null);
  }, [fromCurrency, toCurrency]);

  // Toggle favorite currency
  const toggleFavorite = useCallback((currencyCode: string) => {
    setFavorites(prev => {
      if (prev.includes(currencyCode)) {
        return prev.filter(code => code !== currencyCode);
      } else {
        return [...prev, currencyCode];
      }
    });
  }, []);

  const clearHistory = useCallback(() => {
    if (user) {
      clearServerHistoryMutation.mutate();
    } else {
      setConversionHistory([]);
    }
  }, [user, clearServerHistoryMutation]);

  // Get currency symbol
  const getCurrencySymbol = useCallback((code: string) => {
    const currency = currencies.find(c => c.code === code);
    return currency?.symbol || code;
  }, [currencies]);

  // Get currency name
  const getCurrencyName = useCallback((code: string) => {
    const currency = currencies.find(c => c.code === code);
    return currency?.name || code;
  }, [currencies]);

  // Get currency type
  const getCurrencyType = useCallback((code: string) => {
    const currency = currencies.find(c => c.code === code);
    return currency?.type || 'fiat';
  }, [currencies]);

  // Format number based on currency type
  const formatNumber = useCallback((num: number, currencyCode?: string) => {
    const isCrypto = currencyCode ? getCurrencyType(currencyCode) === 'crypto' : false;
    
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: isCrypto ? 8 : 2,
      maximumFractionDigits: isCrypto ? 8 : 2,
    }).format(num);
  }, [getCurrencyType]);

  // Clear result when currencies change (manual conversion only)
  useEffect(() => {
    setResult(null);
    setError("");
  }, [fromCurrency, toCurrency]);

  // Filter currencies by favorites
  const favoriteCurrencies = useMemo(() => {
    return currencies.filter(currency => favorites.includes(currency.code));
  }, [currencies, favorites]);

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 animate-slide-in-left">Currency Converter</h1>
          <p className="text-muted-foreground text-lg animate-slide-in-right">
            Convert between fiat and crypto currencies with real-time rates
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as CurrencyTabValue)} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="converter">Converter</TabsTrigger>
            <TabsTrigger value="favorites">Favorites</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="converter" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Converter */}
              <div className="lg:col-span-2">
                <Card className="card-enhanced">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5" />
                      <span>Currency Converter</span>
                      {lastUpdated && (
                        <Badge variant="outline" className="text-xs">
                          Updated {lastUpdated instanceof Date ? lastUpdated.toLocaleTimeString() : new Date(lastUpdated).toLocaleTimeString()}
                        </Badge>
                      )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => exchangeRateQuery.refetch()}
                        disabled={exchangeRateQuery.isFetching}
                        className="h-8 w-8 p-0"
                      >
                        <RefreshCw className={`h-4 w-4 ${exchangeRateQuery.isFetching ? 'animate-spin' : ''}`} />
                      </Button>
                    </CardTitle>
                    <CardDescription>
                      Convert between 150+ currencies and cryptocurrencies
                      {exchangeRateQuery.isFetching && (
                        <span className="ml-2 text-blue-600">• Updating rates...</span>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Amount Input */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Amount</label>
                      <Input
                        type="number"
                        placeholder="Enter amount"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="text-lg"
                      />
                    </div>

                    {/* Currency Selection */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">From</label>
                        <Select value={fromCurrency} onValueChange={setFromCurrency} disabled={supportedCurrenciesQuery.isLoading}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <div className="font-semibold text-sm p-2 border-b flex items-center justify-between">
                              <span>Fiat Currencies</span>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => toggleFavorite(fromCurrency)}
                                className="h-6 w-6 p-0"
                              >
                                <Star className={`h-4 w-4 ${favorites.includes(fromCurrency) ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                              </Button>
                            </div>
                            {currencies.filter(c => c.type === 'fiat').map(currency => (
                              <SelectItem key={currency.code} value={currency.code}>
                                <div className="flex items-center gap-2">
                                  <span>{currency.flag}</span>
                                  <span>{currency.symbol}</span>
                                  <span>{currency.code}</span>
                                  <span className="text-muted-foreground">({currency.name})</span>
                                </div>
                              </SelectItem>
                            ))}
                            <div className="font-semibold text-sm p-2 border-b">Cryptocurrencies</div>
                            {currencies.filter(c => c.type === 'crypto').map(currency => (
                              <SelectItem key={currency.code} value={currency.code}>
                                <div className="flex items-center gap-2">
                                  <Bitcoin className="h-4 w-4" />
                                  <span>{currency.symbol}</span>
                                  <span>{currency.code}</span>
                                  <span className="text-muted-foreground">({currency.name})</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">To</label>
                        <div className="flex gap-2">
                          <Select value={toCurrency} onValueChange={setToCurrency} disabled={supportedCurrenciesQuery.isLoading}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <div className="font-semibold text-sm p-2 border-b flex items-center justify-between">
                                <span>Fiat Currencies</span>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => toggleFavorite(toCurrency)}
                                  className="h-6 w-6 p-0"
                                >
                                  <Star className={`h-4 w-4 ${favorites.includes(toCurrency) ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                                </Button>
                              </div>
                              {currencies.filter(c => c.type === 'fiat').map(currency => (
                                <SelectItem key={currency.code} value={currency.code}>
                                  <div className="flex items-center gap-2">
                                    <span>{currency.flag}</span>
                                    <span>{currency.symbol}</span>
                                    <span>{currency.code}</span>
                                    <span className="text-muted-foreground">({currency.name})</span>
                                  </div>
                                </SelectItem>
                              ))}
                              <div className="font-semibold text-sm p-2 border-b">Cryptocurrencies</div>
                              {currencies.filter(c => c.type === 'crypto').map(currency => (
                                <SelectItem key={currency.code} value={currency.code}>
                                  <div className="flex items-center gap-2">
                                    <Bitcoin className="h-4 w-4" />
                                    <span>{currency.symbol}</span>
                                    <span>{currency.code}</span>
                                    <span className="text-muted-foreground">({currency.name})</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            onClick={swapCurrencies}
                            variant="outline"
                            size="icon"
                            className="hover-lift"
                          >
                            <ArrowLeftRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Convert Button */}
                    <Button
                      onClick={convertCurrency}
                      className="w-full btn-enhanced hover-lift"
                      disabled={isLoading || !amount}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Converting...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Convert
                        </>
                      )}
                    </Button>

                    {/* Result */}
                    {result !== null && (
                      <div className="space-y-4">
                        <div className="text-center p-6 bg-muted/50 rounded-lg">
                          <div className="text-3xl font-bold">
                            {formatNumber(result, toCurrency)} {getCurrencySymbol(toCurrency)}
                          </div>
                          <div className="text-sm text-muted-foreground mt-2">
                            {formatNumber(parseFloat(amount), fromCurrency)} {getCurrencySymbol(fromCurrency)} = {formatNumber(result, toCurrency)} {getCurrencySymbol(toCurrency)}
                          </div>
                        </div>

                        {exchangeRate && (
                          <div className="text-center space-y-2">
                            <div className="text-sm text-muted-foreground">
                              Exchange Rate: 1 {fromCurrency} = {formatNumber(exchangeRate, toCurrency)} {toCurrency}
                            </div>
                            {exchangeRateQuery.isFetching && (
                              <div className="text-xs text-blue-600 flex items-center justify-center gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Updating rate...
                              </div>
                            )}
                            {exchangeRateQuery.error && (
                              <div className="text-xs text-red-600">
                                Rate update failed - using cached data
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {error && (
                      <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                        <AlertDescription className="text-red-800 dark:text-red-200">
                          {error}
                        </AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Popular Conversions */}
              <div className="lg:col-span-1">
                <Card className="card-enhanced">
                  <CardHeader>
                    <CardTitle>Popular Conversions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {popularConversionsQuery.isLoading ? (
                      <div className="space-y-2">
                        {[...Array(5)].map((_, index) => (
                          <div key={index} className="flex justify-between items-center p-2 rounded">
                            <div className="h-4 bg-muted animate-pulse rounded w-20"></div>
                            <div className="h-4 bg-muted animate-pulse rounded w-16"></div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      popularConversions.map((conversion, index) => (
                      <div key={index} className="flex justify-between items-center p-2 rounded hover:bg-muted/50">
                        <div className="text-sm">
                          <span className="font-medium">{conversion.from}</span>
                          <span className="text-muted-foreground"> → {conversion.to}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {conversion.trend === 'up' && <TrendingUp className="h-3 w-3 text-green-500" />}
                          {conversion.trend === 'down' && <TrendingDown className="h-3 w-3 text-red-500" />}
                          <div className="text-sm font-mono">
                            {formatNumber(conversion.rate, conversion.to)}
                          </div>
                        </div>
                      </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="favorites" className="space-y-6">
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5" />
                  <span>Favorite Currencies</span>
                </CardTitle>
                <CardDescription>
                  Your favorite currencies for quick access
                </CardDescription>
              </CardHeader>
              <CardContent>
                {favoriteCurrencies.length === 0 ? (
                  <div className="text-center py-8">
                    <Star className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No favorite currencies yet</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Click the star icon in the converter to add currencies to your favorites
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {favoriteCurrencies.map(currency => (
                      <div key={currency.code} className="p-4 border rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {currency.flag && <span className="text-xl">{currency.flag}</span>}
                          {currency.type === 'crypto' && <Bitcoin className="h-5 w-5" />}
                          <div>
                            <div className="font-medium">{currency.code}</div>
                            <div className="text-sm text-muted-foreground">{currency.name}</div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleFavorite(currency.code)}
                        >
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  <span>Conversion History</span>
                  {displayHistory.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearHistory}
                      disabled={clearServerHistoryMutation.isPending}
                    >
                      {clearServerHistoryMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Clearing…
                        </>
                      ) : (
                        "Clear History"
                      )}
                    </Button>
                  )}
                </CardTitle>
                <CardDescription>
                  {user
                    ? "Saved to your account when signed in (up to 100 recent conversions)."
                    : "Stored on this device only. Sign in to sync history to your account."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {user && serverHistoryQuery.isLoading ? (
                  <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span>Loading history…</span>
                  </div>
                ) : displayHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No conversion history yet</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Your conversions will appear here once you start using the converter
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {displayHistory.map((item) => (
                      <div key={item.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium">
                              {formatNumber(item.amount, item.from)} {item.from} → {formatNumber(item.result, item.to)} {item.to}
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                              Rate: 1 {item.from} = {formatNumber(item.rate, item.to)} {item.to}
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {item.timestamp instanceof Date 
                              ? `${item.timestamp.toLocaleDateString()} ${item.timestamp.toLocaleTimeString()}`
                              : `${new Date(item.timestamp).toLocaleDateString()} ${new Date(item.timestamp).toLocaleTimeString()}`
                            }
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Card className="card-enhanced mt-6">
          <CardHeader>
            <CardTitle>Features</CardTitle>
            <CardDescription>
              What you can do with this currency converter
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <h3 className="font-semibold">Real-time Exchange Rates</h3>
                <p className="text-sm text-muted-foreground">
                  Get up-to-date exchange rates for 150+ currencies and major cryptocurrencies.
                </p>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold">Fiat & Crypto Support</h3>
                <p className="text-sm text-muted-foreground">
                  Convert between traditional currencies and popular cryptocurrencies like Bitcoin and Ethereum.
                </p>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold">Conversion History</h3>
                <p className="text-sm text-muted-foreground">
                  Signed-in users keep history in Supabase; otherwise it stays in this browser only.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function CurrencyConverterPage() {
  return (
    <Suspense fallback={<div className="container mx-auto px-4 py-8">Loading...</div>}>
      <CurrencyConverterPageContent />
    </Suspense>
  );
}